import React from 'react';

interface Props {
  tabs: {
    label: string;
    onClick: () => void;
    selected: boolean;
    hidden?: boolean;
  }[];
  disabled?: boolean;
}

const HorizontalTabs = ({ tabs, disabled }: Props) => {
  const [showLeftArrow, setShowLeftArrow] = React.useState(false);
  const [showRightArrow, setShowRightArrow] = React.useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const checkForScrollArrows = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    }
  }, []);

  React.useEffect(() => {
    checkForScrollArrows();
    window.addEventListener('resize', checkForScrollArrows);
    return () => window.removeEventListener('resize', checkForScrollArrows);
  }, [checkForScrollArrows]);

  const handleScroll = () => {
    checkForScrollArrows();
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative">
      {showLeftArrow && (
        <button
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-surface-base rounded-full p-1 shadow-md"
          onClick={scrollLeft}
          aria-label="Scroll left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className="gap-2 flex flex-row overflow-x-auto noScrollbar"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
        onScroll={handleScroll}
      >
        {tabs.map((tab, index) => (
          (!tab.hidden && <div
            key={index}
            className={`w-full h-full py-2 px-3 text-center hover:cursor-pointer whitespace-nowrap transition-colors text-text-primary rounded-t-3xl ${tab.selected ? 'border-b-[3px] border-primary-500' : 'border-b-[3px] border-border-tertiary hover:bg-surface-secondary'} ${disabled ? 'opacity-60' : ''}`}
            onClick={disabled ? undefined : tab.onClick}
          >
            <p>{tab.label}</p>
          </div>)
        ))}
      </div>

      {showRightArrow && (
        <button
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-surface-base rounded-full p-1 shadow-md"
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      )}
    </div>
  );
};

export default HorizontalTabs;
